<template>
  <main class="chat-panel">
    <!-- 头部 -->
    <header class="chat-header">
      <div class="header-info">
        <span class="header-icon">{{ assistant ? assistant.icon : '💬' }}</span>
        <div class="header-text">
          <h2 class="header-title">
            {{ topic ? topic.title : '选择或新建话题开始聊天' }}
          </h2>
          <p v-if="assistant" class="header-sub">{{ assistant.name }}</p>
        </div>
      </div>
    </header>

    <!-- 消息区域 -->
    <div ref="messageList" class="message-area">
      <div v-if="!topic" class="welcome">
        <div class="welcome-icon">🤖</div>
        <h3>欢迎使用 AI 聊天室</h3>
        <p>从左侧选择助手和话题，或新建话题开始对话</p>
      </div>

      <template v-else>
        <ChatMessage
          v-for="msg in messages"
          :key="msg.id"
          :message="msg"
          @retry="handleRetryMessage"
          @delete="handleDeleteMessage"
        />
        <div v-if="loading" class="typing-indicator">
          <span></span><span></span><span></span>
        </div>
      </template>
    </div>

    <!-- 输入区域 -->
    <footer class="chat-footer">
      <div class="input-card">
        <!-- 预览区域 -->
        <div v-if="previewImage" class="preview-bar">
          <img :src="previewImage" alt="预览" class="preview-img" />
          <button class="preview-remove" @click="clearPreview">✕</button>
        </div>

        <!-- 文本输入 -->
        <textarea
          ref="textarea"
          v-model="inputText"
          class="chat-input"
          :placeholder="inputPlaceholder"
          rows="1"
          @keydown="handleKeydown"
          @input="autoResize"
        ></textarea>

        <!-- 底部工具栏 -->
        <div class="bottom-bar">
          <div class="bar-left">
            <!-- 智能搜索 -->
            <button
              class="websearch-btn"
              :class="{ active: webSearchEnabled, disabled: !isLoggedIn }"
              :disabled="!isLoggedIn"
              :title="isLoggedIn ? '联网搜索' : '请先登录'"
              @click="isLoggedIn && (webSearchEnabled = !webSearchEnabled)"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
              <span>智能搜索</span>
            </button>

            <!-- 使用命令 -->
            <div class="menu-wrapper" ref="commandMenuRef">
              <button
                class="bar-btn"
                :class="{ active: showCommandMenu, disabled: !isLoggedIn }"
                :disabled="!isLoggedIn"
                :title="isLoggedIn ? '使用命令' : '请先登录'"
                @click="isLoggedIn && toggleCommandMenu()"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <rect x="3" y="3" width="7" height="7" rx="1.5"/>
                  <rect x="14" y="3" width="7" height="7" rx="1.5"/>
                  <rect x="3" y="14" width="7" height="7" rx="1.5"/>
                  <rect x="14" y="14" width="7" height="7" rx="1.5"/>
                </svg>
              </button>
              <div v-if="showCommandMenu" class="popup-menu">
                <div
                  v-for="cmd in commandList"
                  :key="cmd.key"
                  class="menu-item"
                  @click="selectCommand(cmd)"
                >
                  <span class="menu-icon">{{ cmd.icon }}</span>
                  <span class="menu-label">{{ cmd.label }}</span>
                </div>
              </div>
            </div>

            <!-- 上传图片 -->
            <label class="bar-btn" :class="{ disabled: !isLoggedIn }" :title="isLoggedIn ? '上传图片' : '请先登录'">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
              </svg>
              <input
                v-if="isLoggedIn"
                ref="imageInput"
                type="file"
                accept="image/*"
                hidden
                @change="handleImageSelect"
              />
            </label>
          </div>

          <div class="bar-right">
            <!-- 切换模型 -->
            <div class="menu-wrapper" ref="modelMenuRef">
              <button
                class="bar-btn model-btn"
                :class="{ active: showModelMenu }"
                :title="isLoggedIn ? '切换模型' : '登录后可切换模型'"
                @click="toggleModelMenu"
              >
                <span class="model-label">{{ currentModel.label }}</span>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                  <path d="M7 10l5 5 5-5z"/>
                </svg>
              </button>
              <div v-if="showModelMenu" class="popup-menu model-menu">
                <div
                  v-for="m in modelList"
                  :key="m.key"
                  class="menu-item"
                  :class="{ selected: currentModel.key === m.key, locked: !isLoggedIn && m.key !== 'auto' }"
                  @click="selectModel(m)"
                >
                  <span class="menu-icon">{{ m.icon }}</span>
                  <div class="model-info">
                    <span class="model-name">{{ m.label }}</span>
                    <span class="model-desc">{{ m.desc }}</span>
                  </div>
                  <span v-if="currentModel.key === m.key" class="check-icon">✓</span>
                  <span v-else-if="!isLoggedIn && m.key !== 'auto'" class="lock-icon" title="登录后可切换">🔒</span>
                </div>
              </div>
            </div>

            <!-- 发送按钮 -->
            <button
              class="send-btn"
              :disabled="!canSend || loading"
              @click="handleSend"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </footer>
  </main>
</template>

<script>
import ChatMessage from './ChatMessage.vue'
import { readFileAsDataURL } from '../utils/helpers'
import { getSystemLlms } from '../api'

export default {
  name: 'ChatPanel',
  components: { ChatMessage },
  props: {
    assistant: { type: Object, default: null },
    topic: { type: Object, default: null },
    messages: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    isLoggedIn: { type: Boolean, default: false }
  },
  data() {
    return {
      inputText: '',
      previewImage: null,
      showCommandMenu: false,
      showModelMenu: false,
      webSearchEnabled: true,
      currentModel: {
        key: 'auto',
        label: 'Auto Mode',
        icon: '✨',
        desc: '自动选择最佳模型'
      },
      modelList: [
        { key: 'auto', label: 'Auto Mode', icon: '✨', desc: '自动选择最佳模型' }
      ],
      commandList: [
        { key: 'code', label: '代码解释', icon: '💻' },
        { key: 'translate', label: '翻译', icon: '🌐' },
        { key: 'summarize', label: '总结', icon: '📋' },
        { key: 'brainstorm', label: '头脑风暴', icon: '💡' },
        { key: 'write', label: '写作助手', icon: '✍️' },
        { key: 'analyze', label: '数据分析', icon: '📊' }
      ]
    }
  },
  computed: {
    canSend() {
      return this.inputText.trim() || this.previewImage
    },
    inputPlaceholder() {
      return '输入消息，Enter 发送，Shift+Enter 换行...'
    }
  },
  watch: {
    messages: {
      handler() {
        this.$nextTick(this.scrollToBottom)
      },
      deep: true
    },
    loading() {
      this.$nextTick(this.scrollToBottom)
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
    this.loadSystemModels()
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    // 从后端加载系统可用模型列表
    loadSystemModels() {
      var self = this
      getSystemLlms().then(function (json) {
        if (json && (json.code === '200' || json.code === 200 || json.success) && Array.isArray(json.data)) {
          var list = [{ key: 'auto', label: 'Auto Mode', icon: '✨', desc: '自动选择最佳模型' }]
          json.data.forEach(function (item) {
            if (!item) return
            list.push({
              key: item.llmCode || ('llm-' + item.id),
              label: item.name || item.llmCode || ('模型' + item.id),
              icon: '🤖',
              desc: item.llmCode || ''
            })
          })
          self.modelList = list
        }
      }).catch(function (err) {
        console.error('加载系统模型列表失败:', err)
      })
    },
    scrollToBottom() {
      const el = this.$refs.messageList
      if (el) el.scrollTop = el.scrollHeight
    },
    autoResize() {
      const ta = this.$refs.textarea
      if (!ta) return
      ta.style.height = 'auto'
      ta.style.height = Math.min(ta.scrollHeight, 140) + 'px'
    },
    handleKeydown(e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        this.handleSend()
      }
    },
    handleClickOutside(e) {
      if (!this.showCommandMenu && !this.showModelMenu) return
      const cmdRef = this.$refs.commandMenuRef
      const modelRef = this.$refs.modelMenuRef
      if (cmdRef && !cmdRef.contains(e.target)) this.showCommandMenu = false
      if (modelRef && !modelRef.contains(e.target)) this.showModelMenu = false
    },
    toggleCommandMenu() {
      this.showCommandMenu = !this.showCommandMenu
      this.showModelMenu = false
    },
    toggleModelMenu() {
      this.showModelMenu = !this.showModelMenu
      this.showCommandMenu = false
    },
    selectCommand(cmd) {
      this.inputText = `/${cmd.key} `
      this.showCommandMenu = false
      this.$nextTick(() => {
        const ta = this.$refs.textarea
        if (ta) ta.focus()
      })
    },
    selectModel(m) {
      // 未登录时仅允许使用 Auto Mode，其他模型不可切换
      if (!this.isLoggedIn && m.key !== 'auto') return
      this.currentModel = m
      this.showModelMenu = false
    },
    async handleImageSelect(e) {
      const file = e.target.files[0]
      if (!file) return
      if (!file.type.startsWith('image/')) {
        alert('请选择图片文件')
        return
      }
      this.previewImage = await readFileAsDataURL(file)
      e.target.value = ''
    },
    clearPreview() {
      this.previewImage = null
    },
    handleSend() {
      if (!this.canSend || this.loading) return

      let type = 'text'
      let content = this.inputText.trim()

      if (this.previewImage) {
        type = 'image'
        content = this.previewImage
      }

      this.$emit('send', {
        content,
        type,
        model: this.currentModel.key,
        webSearch: this.webSearchEnabled
      })

      this.inputText = ''
      this.previewImage = null
      this.$nextTick(() => {
        const ta = this.$refs.textarea
        if (ta) ta.style.height = 'auto'
      })
    },
    handleRetryMessage(message) {
      this.$emit('message-retry', message)
    },
    handleDeleteMessage(message) {
      this.$emit('message-delete', message)
    }
  }
}
</script>

<style scoped>
.chat-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #fff;
  min-width: 0;
  min-height: 0;
  height: 100%;
  overflow: hidden;
}

.chat-header {
  padding: 16px 24px;
  border-bottom: 1px solid #e8e8e8;
  flex-shrink: 0;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon {
  font-size: 28px;
}

.header-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.header-sub {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
}

.message-area {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px;
}

.welcome {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  text-align: center;
}

.welcome-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.welcome h3 {
  font-size: 18px;
  color: #666;
  margin-bottom: 8px;
}

.welcome p {
  font-size: 14px;
}

.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 12px 16px;
  margin-bottom: 12px;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  background: #ccc;
  border-radius: 50%;
  animation: bounce 1.4s infinite ease-in-out both;
}

.typing-indicator span:nth-child(1) { animation-delay: -0.32s; }
.typing-indicator span:nth-child(2) { animation-delay: -0.16s; }

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
}

/* ====== 输入区 ====== */
.chat-footer {
  padding: 12px 24px 16px;
  flex-shrink: 0;
}

.input-card {
  background: #fff;
  border: 1.5px solid #e4e4e7;
  border-radius: 20px;
  padding: 12px 14px 8px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.input-card:focus-within {
  border-color: #d4d4d8;
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.04);
}

.chat-input {
  width: 100%;
  padding: 8px 4px 6px;
  border: none;
  resize: none;
  font-size: 14px;
  line-height: 1.6;
  max-height: 160px;
  outline: none;
  background: transparent;
  color: #1a1a1a;
}

.chat-input::placeholder {
  color: #9ca3af;
}

/* 预览 */
.preview-bar {
  position: relative;
  display: inline-block;
  margin: 4px 0 4px 4px;
}

.preview-img {
  max-width: 100px;
  max-height: 80px;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
}

.preview-remove {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 18px;
  height: 18px;
  background: #666;
  color: #fff;
  border-radius: 50%;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

/* 底部工具栏 */
.bottom-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 4px 2px;
  border-top: 1px solid #f0f0f0;
  margin-top: 4px;
}

.bar-left,
.bar-right {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* 智能搜索按钮 */
.websearch-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 14px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #6366f1;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.18s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.websearch-btn:hover {
  border-color: #c7d2fe;
  background: #eef2ff;
  color: #4f46e5;
}

.websearch-btn.active {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border-color: transparent;
  color: #fff;
  box-shadow: 0 2px 6px rgba(99, 102, 241, 0.35);
}

.websearch-btn.active:hover {
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
}

.websearch-btn svg {
  flex-shrink: 0;
}

/* 工具栏按钮 */
.bar-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 10px;
  color: #6b7280;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.18s ease;
  position: relative;
}

.bar-btn:hover {
  background: #f3f4f6;
  color: #374151;
}

.bar-btn.active {
  background: #e5e7eb;
  color: #111827;
}

/* 禁用态（未登录） */
.bar-btn.disabled,
.websearch-btn.disabled,
.bar-btn:disabled,
.websearch-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed !important;
  pointer-events: auto;
}

.bar-btn.disabled:hover,
.websearch-btn.disabled:hover,
.bar-btn:disabled:hover,
.websearch-btn:disabled:hover {
  background: transparent;
  color: inherit;
}

/* 模型按钮 */
.model-btn {
  padding: 6px 12px;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
}

.model-btn:hover {
  background: #f3f4f6;
}

.model-label {
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 发送按钮 */
.send-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #1a1a1a;
  color: #fff;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.send-btn:hover:not(:disabled) {
  background: #374151;
  transform: scale(1.05);
}

.send-btn:disabled {
  background: #d1d5db;
  cursor: not-allowed;
}

/* 弹出菜单 */
.menu-wrapper {
  position: relative;
  display: inline-flex;
}

.popup-menu {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  min-width: 180px;
  background: #fff;
  border: 1px solid #e4e4e7;
  border-radius: 12px;
  padding: 6px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  z-index: 100;
  animation: fadeInUp 0.15s ease;
}

.popup-menu.model-menu {
  min-width: 240px;
  left: auto;
  right: 0;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;
  font-size: 13px;
  color: #374151;
}

.menu-item:hover {
  background: #f3f4f6;
}

.menu-item.selected {
  background: #f0fbf9;
  color: #1a9c8e;
}

.menu-icon {
  font-size: 16px;
  width: 20px;
  text-align: center;
}

.menu-label {
  flex: 1;
}

.model-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.model-name {
  font-weight: 500;
  font-size: 13px;
  color: #1a1a1a;
}

.model-desc {
  font-size: 11px;
  color: #9ca3af;
}

.check-icon {
  color: #1a9c8e;
  font-size: 14px;
  font-weight: 600;
}

.lock-icon {
  font-size: 12px;
  opacity: 0.5;
}

.menu-item.locked {
  opacity: 0.55;
  cursor: not-allowed;
}

.menu-item.locked:hover {
  background: transparent;
}

/* ====== 移动端 ≤768px ====== */
@media (max-width: 768px) {
  .chat-panel {
    flex: 1;
    min-height: 0;
  }

  .chat-header {
    display: none;
  }

  .message-area {
    padding: 16px 12px;
  }

  .welcome-icon {
    font-size: 40px;
  }

  .welcome h3 {
    font-size: 16px;
  }

  .chat-footer {
    padding: 8px 10px 12px;
    padding-bottom: calc(12px + env(safe-area-inset-bottom, 0));
  }

  .input-card {
    border-radius: 16px;
    padding: 10px 10px 6px;
  }

  .chat-input {
    font-size: 16px; /* 防止 iOS 缩放 */
  }

  .bottom-bar {
    gap: 4px;
  }

  .bar-left,
  .bar-right {
    gap: 2px;
  }

  .bar-btn {
    padding: 6px 8px;
  }

  .model-btn {
    padding: 6px 8px;
  }

  .model-label {
    max-width: 60px;
    font-size: 12px;
  }

  .send-btn {
    width: 32px;
    height: 32px;
  }

  .popup-menu {
    min-width: 150px;
  }

  .popup-menu.model-menu {
    min-width: 200px;
  }
}

/* ====== 平板 769px~1024px ====== */
@media (min-width: 769px) and (max-width: 1024px) {
  .message-area {
    padding: 20px 16px;
  }

  .chat-footer {
    padding: 12px 16px 16px;
  }
}
</style>
